import React from 'react';

import { SURFACE, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, ButtonBase } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

const PADDING_UNITS: Record<'card' | 'plate' | 'none', number> = {
  card: SURFACE.cardPadding / 8,
  plate: SURFACE.platePadding / 8,
  none: 0,
};

export type SurfacePlateProps = {
  children?: React.ReactNode;
  /** 'card' is the bigger clickable area (a domain card); 'plate' (default) is a compact info panel. */
  padding?: 'card' | 'plate' | 'none';
  /** Hover wash and a focus-visible ring; renders as a native button so it is keyboard-operable. */
  interactive?: boolean;
  onClick?: () => void;
  /** Native compiled report destinations use ordinary links in the same card shell. */
  href?: string;
  className?: string;
  sx?: SxProps<Theme>;
  'aria-label'?: string;
};

/**
 * The one plate/card shell for this surface: Compass White, a single hairline border, one radius,
 * no shadow. Values are Business Pulse's own (CatalogueList.tsx:202,219,254,307,312,314-315),
 * exposed as `SURFACE` in unifiedAnalyticsTheme.ts. `interactive` reproduces the domain card's own
 * hover/focus formula exactly (CatalogueList.tsx:314-315).
 */
export default function SurfacePlate({
  'aria-label': ariaLabel,
  children,
  className,
  interactive = false,
  href,
  onClick,
  padding = 'plate',
  sx,
}: SurfacePlateProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const Component = (interactive ? ButtonBase : Box) as React.ElementType;

  return (
    <Component
      aria-label={ariaLabel}
      className={className}
      component={interactive && href ? 'a' : undefined}
      href={interactive ? href : undefined}
      onClick={interactive ? onClick : undefined}
      sx={[
        {
          backgroundColor: tokens.surface,
          border: `1px solid ${alpha(tokens.accent, SURFACE.hairlineAlpha)}`,
          borderRadius: `${SURFACE.radius}px`,
          p: PADDING_UNITS[padding],
        },
        interactive
          ? {
              display: 'flex',
              textAlign: 'left',
              '&:hover': { backgroundColor: alpha(tokens.accent, 0.035) },
              '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: -2 },
            }
          : {},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Component>
  );
}
